import VendorShipment, {
  findTrackingLine,
  shipmentOutstanding,
  shipmentLinesCorrupted,
} from "../../../classes/VendorShipment";
import { db } from "../../../boot/firebase";
import { doc, runTransaction } from "firebase/firestore";
import { codesMatch } from "../../../util/barcode";

import { call, EXTRACT_ACK_TIMEOUT_MS } from "../../../util/misc";

export const GET_VENDOR_SHIPMENTS = (
  { dispatch },
  { projectID, saleID, orderID }
) => {
  return dispatch("GET_COLLECTION", {
    converter: VendorShipment,
    stateKey: "vendorShipments",
    path:
      projectID && saleID && orderID
        ? VendorShipment.getPath(projectID, saleID, orderID)
        : null,
  });
};

export const LISTEN_VENDOR_SHIPMENTS = (
  { dispatch },
  { projectID, saleID, orderID, next, error, unlisten }
) => {
  return dispatch("LISTEN_COLLECTION", {
    converter: VendorShipment,
    stateKey: "vendorShipments",
    path:
      projectID && saleID && orderID
        ? VendorShipment.getPath(projectID, saleID, orderID)
        : null,
    next,
    error,
    unlisten,
  });
};

/**
 * Trigger a sync of vendor shipping data for an order.
 * Calls the syncVendorShipping Cloud Function.
 *
 * `acceptVendorPO` (`{ ackID, vendorPO }`) is the office's "This order is
 * ours" for an ack flagged `unverifiable`: the function records it before it
 * syncs, since a locked ack cannot be updated from the page (AckView).
 */
export const SYNC_VENDOR_SHIPPING = async (
  { dispatch },
  { projectID, saleID, orderID, vendorOrderNumber, acceptVendorPO }
) => {
  return call("syncVendorShipping", {
    projectID,
    saleID,
    orderID,
    vendorOrderNumber,
    ...(acceptVendorPO ? { acceptVendorPO } : {}),
  });
};

/**
 * Ask Marvin for everything on a truck the dock has just identified, so the
 * session expects the whole load -- including orders whose ack was mistyped
 * or never entered, which the per-ack poll cannot know about. Resolves to
 * `{ truckNumber, orders, unplaced }`; the stored shipments reach the page
 * through its listener.
 */
export const SYNC_VENDOR_TRUCK = async (_, { truckNumber }) => {
  return call("syncVendorTruck", { truckNumber });
};

/**
 * Extract data from an uploaded ack document using Gemini.
 * Calls the extractAckData Cloud Function.
 */
export const EXTRACT_ACK_DATA = async (
  { dispatch },
  { documentBase64, mimeType, vendor }
) => {
  return call(
    "extractAckData",
    { documentBase64, mimeType, vendor },
    { timeout: EXTRACT_ACK_TIMEOUT_MS }
  );
};

/**
 * Stamp one tracking line inside a vendor shipment: read the whole document
 * in a transaction, change the one package, write `lines` back as an array.
 *
 * Firestore cannot address an array element by index in a field path. The
 * update this replaced -- `updateDoc(ref, {"lines.0.trackingLines.2
 * .receivedAt": v})` -- did not touch slot 0: it rebuilt `lines` as a map
 * holding only that path and dropped every other line and package on the
 * truck (see VendorShipment.normalizeShipmentLines). Reading and writing the
 * whole array is the write Firestore actually supports, and the transaction
 * keeps two scanners on the same truck from overwriting each other's stamps.
 *
 * A document the old write already truncated is left alone: index 0 of the
 * remnant is not the package the scan named, so a stamp would land on the
 * wrong line. The caller gets `corrupted: true` plus the vendor order number
 * it needs to ask for a fresh copy (RESYNC_VENDOR_SHIPMENTS).
 *
 * Not an action (camelCase, so onlyActions drops it): RECORD_SCAN and
 * DELETE_SCAN in receivingSessions.js share it with UPDATE_TRACKING_LINE, so
 * there is exactly one way this document gets written from a scan.
 *
 * `trackingNo` names the package the indices are meant to point at. A sync
 * rewrites `lines` from Marvin's feed, so between a scan and its undo the
 * package can sit at other indices, or on another truck's document -- the
 * vendor sync moves a receipt when Marvin moves part of a load
 * (writeShipments in functions/vendorShippingSync.js). The package at the
 * indices is stamped only when it is that package (codesMatch, so a scan
 * that recorded a piece-marked label still finds its own); otherwise the
 * document is searched by tracking number, and when it no longer lists the
 * package at all nothing is written and the caller hears `unlisted: true`.
 * Without indices, the tracking number alone finds it. `onlyIf(trackingLine)`
 * is checked against the copy read inside the transaction; when it says no,
 * nothing is written (`skipped: true`).
 */
export const stampTrackingLine = (
  shipmentRef,
  lineIndex,
  trackingIndex,
  patch,
  meta,
  { trackingNo = null, onlyIf = null } = {}
) =>
  runTransaction(db, async (tx) => {
    const snapshot = await tx.get(shipmentRef);
    if (!snapshot.exists()) return { stamped: false, missing: true };
    const stored = snapshot.data();
    if (shipmentLinesCorrupted(stored.lines)) {
      return {
        stamped: false,
        corrupted: true,
        vendorOrderNumber: stored.vendorOrderNumber || "",
      };
    }
    const lines = Array.isArray(stored.lines) ? stored.lines : [];
    const line = lineIndex == null ? null : lines[lineIndex];
    let trackingLine =
      line && Array.isArray(line.trackingLines)
        ? line.trackingLines[trackingIndex] || null
        : null;
    if (
      trackingNo &&
      !(trackingLine && codesMatch(trackingNo, trackingLine.trackingNo))
    ) {
      trackingLine = findTrackingLine(lines, trackingNo);
      if (!trackingLine) return { stamped: false, unlisted: true };
    }
    if (!trackingLine) return { stamped: false };
    if (onlyIf && !onlyIf(trackingLine)) {
      return { stamped: false, skipped: true };
    }
    Object.assign(trackingLine, patch);
    // `outstanding` is the denormalized count the pending listener filters
    // on; recompute it from the lines actually written so a stamp moves the
    // document in or out of that query. A received order stays at zero.
    tx.update(shipmentRef, {
      lines,
      outstanding: shipmentOutstanding({ ...stored, lines }),
      ...meta,
    });
    return { stamped: true };
  });

/**
 * Ask the vendor for a fresh copy of the shipments on these orders.
 *
 * This is the only repair for a shipment document the array-index bug
 * truncated: the write dropped every other line, so nothing client-side can
 * rebuild it, but syncVendorShipping rewrites `lines` from Marvin's feed. The
 * receiving page asks for every shipment that arrives corrupted, and the scan
 * stamps ask when they run into one.
 *
 * `shipments` are VendorShipment instances (only the corrupted ones count);
 * `orders` are bare `{ projectID, saleID, orderID, vendorOrderNumber }`. One
 * request per order at a time -- a truck's worth of snapshots or scans must
 * not fan out into a sync per package -- and never a throw: the sync is a
 * repair in the background, and the page keeps working on the coerced copy
 * meanwhile. Resolves to the number of syncs requested.
 */
const resyncInFlight = new Map();

export const RESYNC_VENDOR_SHIPMENTS = async (
  { dispatch },
  { orders = [], shipments = [] } = {}
) => {
  const wanted = new Map();
  shipments
    .filter((shipment) => shipment && shipment.linesCorrupted)
    .forEach((shipment) =>
      wanted.set(shipment.orderID, {
        projectID: shipment.projectID,
        saleID: shipment.saleID,
        orderID: shipment.orderID,
        vendorOrderNumber: shipment.data.vendorOrderNumber,
      })
    );
  orders.forEach((order) => order && wanted.set(order.orderID, order));

  let requested = 0;
  for (const order of wanted.values()) {
    if (!order.orderID || !order.vendorOrderNumber) continue;
    if (resyncInFlight.has(order.orderID)) continue;
    requested++;
    const request = dispatch("SYNC_VENDOR_SHIPPING", order)
      .catch((error) => {
        console.warn(
          `Vendor shipment re-sync failed for order ${order.orderID}:`,
          error
        );
      })
      .finally(() => resyncInFlight.delete(order.orderID));
    resyncInFlight.set(order.orderID, request);
  }
  return requested;
};

/**
 * Update a tracking line's receive status (used during barcode scanning in
 * Vendor Receiving Mode). Goes through stampTrackingLine, never a field path
 * indexed into the array; a truncated document is sent for a re-sync instead.
 */
export const UPDATE_TRACKING_LINE = async (
  { state, dispatch },
  { projectID, saleID, orderID, shipmentID, lineIndex, trackingIndex, changes }
) => {
  const path = VendorShipment.getPath(projectID, saleID, orderID, shipmentID);
  const result = await stampTrackingLine(
    doc(db, path),
    lineIndex,
    trackingIndex,
    changes,
    { modified: new Date(), modifiedBy: state.user.id }
  );
  if (result.corrupted) {
    dispatch("RESYNC_VENDOR_SHIPMENTS", {
      orders: [
        {
          projectID,
          saleID,
          orderID,
          vendorOrderNumber: result.vendorOrderNumber,
        },
      ],
    });
  }
  return result;
};
